import { z } from "zod";

import { isPossiblePhoneNumberInput } from "./phone";

// Customer type enum
export const customerTypeValues = ["individual", "business"] as const;
export type CustomerType = (typeof customerTypeValues)[number];

export const customerCompanyNumberSchemeValues = ["fr_siren", "be_bce"] as const;

// Base schema without refinement for form validation
const baseCustomerFields = {
  customerType: z.enum(customerTypeValues),
  email: z.email("validation.email"),
  firstName: z.string().min(1, "validation.required").max(255),
  lastName: z.string().min(1, "validation.required").max(255),
  companyName: z.string().max(255).optional(),
  companyNumber: z.string().max(64).optional(),
  companyNumberScheme: z.enum(customerCompanyNumberSchemeValues).optional(),
  vatNumber: z.string().max(64).optional(),
  phone: z
    .string()
    .refine((val) => !val || isPossiblePhoneNumberInput(val), {
      message: "validation.phone",
    })
    .optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().length(2, "validation.minLength").optional(),
  notes: z.string().optional(),
};

// Schema factory that accepts translation function
export const createCustomerSchema = (
  t: (key: string, params?: Record<string, string | number | Date>) => string,
) =>
  z
    .object({
      customerType: z.enum(customerTypeValues),
      email: z.email(t("email")),
      firstName: z.string().min(1, t("required")).max(255),
      lastName: z.string().min(1, t("required")).max(255),
      companyName: z.string().max(255).optional(),
      companyNumber: z.string().max(64).optional(),
      companyNumberScheme: z.enum(customerCompanyNumberSchemeValues).optional(),
      vatNumber: z.string().max(64).optional(),
      phone: z
        .string()
        .refine((val) => !val || isPossiblePhoneNumberInput(val), {
          message: t("phone"),
        })
        .optional(),
      address: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      country: z
        .string()
        .length(2, t("minLength", { min: 2 }))
        .optional(),
      notes: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // If business customer, company name is required
      if (
        data.customerType === "business" &&
        (!data.companyName || data.companyName.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("companyNameRequired"),
          path: ["companyName"],
        });
      }
    });

// Default schema for server-side validation (uses keys that client will translate)
export const customerSchema = z.object(baseCustomerFields).superRefine((data, ctx) => {
  // If business customer, company name is required
  if (
    data.customerType === "business" &&
    (!data.companyName || data.companyName.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "validation.companyNameRequired",
      path: ["companyName"],
    });
  }
});

export type CustomerInput = z.infer<typeof customerSchema>;
